import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PERMISSIONS } from '@oxxean/types';
import { executeSync, fetchSyncRunErrors, fetchSyncRuns, fetchSyncStatus } from '../../lib/api/sync';
import { queryKeys } from '../../lib/query-client';
import { errorMessage } from '../../lib/errors';
import { formatDateTime, formatDuration, formatNumber } from '../../lib/format';
import { useAuth } from '../../hooks/useAuth';
import { useTableState } from '../../hooks/useTableState';
import { useToasts } from '../../hooks/useToasts';
import {
  SYNC_RUN_STATUS_LABEL,
  SYNC_SOURCE_LABEL,
  type SyncRun,
  type SyncRunError,
  type SyncSource,
} from '../../types/domain';
import { DataBoundary } from '../../components/DataBoundary';
import { DataTable, type Column } from '../../components/DataTable';
import { SyncSourceDetail } from '../../components/SyncStatusIndicator';
import { ConfirmDialog, Modal } from '../../components/Modal';
import { Badge, Button, Card, Notice, Skeleton, TableSkeleton } from '../../components/primitives';
import { SelectField, TextField } from '../../components/form';
import { PageHeading } from '../layout/PageHeading';

const RUN_TONE: Record<SyncRun['status'], 'green' | 'amber' | 'red' | 'neutral' | 'blue'> = {
  RUNNING: 'blue',
  SUCCESS: 'green',
  PARTIAL: 'amber',
  FAILED: 'red',
  ABORTED: 'red',
};

/**
 * Sincronizaciones (§33).
 *
 * Los errores NO se ocultan: se cuentan en la tabla y se detallan uno por uno en el
 * panel lateral, con recurso, página, código y mensaje. Una sincronización que
 * reporta éxito con datos incompletos es peor que una que falla ruidosamente.
 */
export function SyncPage(): JSX.Element {
  const { can } = useAuth();
  const { push } = useToasts();
  const queryClient = useQueryClient();
  const table = useTableState({ sortBy: 'startedAt', sortDir: 'desc' });

  const [errorRun, setErrorRun] = useState<SyncRun | null>(null);
  const [runDialogOpen, setRunDialogOpen] = useState(false);
  const [source, setSource] = useState<SyncSource>('BUK_CORE');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  const canExecute = can(PERMISSIONS.SYNC_EXECUTE);

  const statusQuery = useQuery({
    queryKey: queryKeys.syncStatus,
    queryFn: fetchSyncStatus,
    refetchInterval: 30_000,
  });

  const runsQuery = useQuery({
    queryKey: queryKeys.syncRuns(table.state),
    queryFn: () => fetchSyncRuns(table.state),
    // Con una ejecución en curso conviene ver avanzar los contadores.
    refetchInterval: 30_000,
  });

  const executeMutation = useMutation({
    mutationFn: () =>
      executeSync({
        source,
        from: from.length > 0 ? from : undefined,
        to: to.length > 0 ? to : undefined,
      }),
    onSuccess: (result) => {
      push({
        kind: 'success',
        title: 'Sincronización solicitada',
        message: `${result.message} La reejecución es segura: el proceso es idempotente.`,
      });
      setConfirmOpen(false);
      setRunDialogOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['sync'] });
    },
    onError: (error) => {
      push({ kind: 'error', title: 'No se pudo iniciar la sincronización', message: errorMessage(error) });
    },
  });

  const columns = useMemo<Array<Column<SyncRun>>>(
    () => [
      {
        key: 'sourceLabel',
        header: 'Fuente',
        sortable: true,
        render: (row) => row.sourceLabel,
        plain: (row) => row.sourceLabel,
      },
      {
        key: 'resource',
        header: 'Recurso',
        sortable: true,
        render: (row) => <span className="mono">{row.resource}</span>,
        plain: (row) => row.resource,
      },
      {
        key: 'period',
        header: 'Período',
        render: (row) =>
          row.periodFrom === null && row.periodTo === null
            ? '—'
            : `${row.periodFrom ?? '—'} a ${row.periodTo ?? '—'}`,
        plain: (row) => row.periodFrom,
      },
      {
        key: 'status',
        header: 'Estado',
        sortable: true,
        render: (row) => <Badge tone={RUN_TONE[row.status]}>{SYNC_RUN_STATUS_LABEL[row.status]}</Badge>,
        plain: (row) => row.status,
      },
      {
        key: 'recordsCreated',
        header: 'Creados',
        align: 'right',
        sortable: true,
        render: (row) => formatNumber(row.recordsCreated),
        plain: (row) => row.recordsCreated,
      },
      {
        key: 'recordsUpdated',
        header: 'Actualizados',
        align: 'right',
        sortable: true,
        render: (row) => formatNumber(row.recordsUpdated),
        plain: (row) => row.recordsUpdated,
      },
      {
        key: 'recordsSkipped',
        header: 'Omitidos',
        align: 'right',
        sortable: true,
        render: (row) => formatNumber(row.recordsSkipped),
        plain: (row) => row.recordsSkipped,
      },
      {
        key: 'recordsFailed',
        header: 'Errores',
        align: 'right',
        sortable: true,
        render: (row) =>
          row.recordsFailed === 0 && row.errorCount === 0 ? (
            <span className="muted">0</span>
          ) : (
            <button
              type="button"
              className="btn btn--sm btn--ghost"
              onClick={() => setErrorRun(row)}
              title="Ver el detalle de los errores"
            >
              <Badge tone="red">{formatNumber(Math.max(row.recordsFailed, row.errorCount))}</Badge>
            </button>
          ),
        plain: (row) => row.recordsFailed,
      },
      {
        key: 'durationMs',
        header: 'Duración',
        align: 'right',
        sortable: true,
        render: (row) => formatDuration(row.durationMs),
        plain: (row) => row.durationMs,
      },
      {
        key: 'startedAt',
        header: 'Inicio',
        sortable: true,
        render: (row) => formatDateTime(row.startedAt),
        plain: (row) => row.startedAt,
      },
      {
        key: 'triggeredBy',
        header: 'Usuario',
        sortable: true,
        render: (row) =>
          row.trigger === 'MANUAL' ? (row.triggeredBy ?? '—') : `(${row.trigger.toLowerCase()})`,
        plain: (row) => row.triggeredBy,
      },
    ],
    [],
  );

  const runs = runsQuery.data;

  return (
    <>
      <PageHeading
        title="Sincronizaciones"
        description="Ejecuciones de sincronización con BUK y BUK Asistencia. Toda ejecución es idempotente: reejecutar el mismo lote produce el mismo estado."
        actions={
          canExecute ? (
            <Button variant="primary" icon="play" onClick={() => setRunDialogOpen(true)}>
              Ejecutar sincronización
            </Button>
          ) : undefined
        }
      />

      <div className="row row--2e">
        <Card title="Estado por origen">
          <DataBoundary
            isLoading={statusQuery.isLoading}
            isError={statusQuery.isError}
            error={statusQuery.error}
            onRetry={() => void statusQuery.refetch()}
            isEmpty={statusQuery.data !== undefined && statusQuery.data.length === 0}
            empty={{
              title: 'Sin orígenes configurados',
              message: 'No hay integraciones registradas todavía.',
            }}
            skeleton={
              <div className="stack">
                <Skeleton height={92} radius={10} />
                <Skeleton height={92} radius={10} />
              </div>
            }
          >
            <div className="sync-list">
              {(statusQuery.data ?? []).map((item) => (
                <SyncSourceDetail key={item.source} item={item} />
              ))}
            </div>
          </DataBoundary>
        </Card>

        <Card title="Reglas vigentes">
          <dl className="def-list">
            <dt>Carga inicial</dt>
            <dd>Desde la fecha configurada hasta hoy, mes a mes.</dd>
            <dt>Incremental</dt>
            <dd>Cada hora, sólo el mes vigente.</dd>
            <dt>Reconciliación</dt>
            <dd>Diaria, reconsultando los últimos días configurados.</dd>
            <dt>Solapamiento</dt>
            <dd>La marca de agua retrocede 5 minutos para absorber diferencias de reloj.</dd>
            <dt>Ausencia en lote</dt>
            <dd>No implica baja: la baja se detecta por estado terminado o por reconciliación.</dd>
            <dt>Concurrencia</dt>
            <dd>Una ejecución activa por fuente, recurso y empresa; la segunda se rechaza.</dd>
          </dl>
          <Notice tone="neutral">
            Si más del 5 % de un lote falla la validación de esquema, la ejecución se aborta y se
            conserva la copia anterior. Es preferible un dato viejo y coherente a uno nuevo y corrupto.
          </Notice>
        </Card>
      </div>

      <Card title="Historial de ejecuciones" flush>
        <DataBoundary
          isLoading={runsQuery.isLoading}
          isError={runsQuery.isError}
          error={runsQuery.error}
          onRetry={() => void runsQuery.refetch()}
          isEmpty={runs !== undefined && runs.total === 0 && table.state.search.length === 0}
          empty={{
            title: 'Sin ejecuciones registradas',
            message: canExecute
              ? 'Todavía no se ha ejecutado ninguna sincronización. Puede lanzar la primera con «Ejecutar sincronización».'
              : 'Todavía no se ha ejecutado ninguna sincronización.',
          }}
          skeleton={<TableSkeleton columns={9} />}
        >
          {runs !== undefined && (
            <DataTable
              caption="Historial de ejecuciones de sincronización"
              columns={columns}
              rows={runs.data}
              getRowId={(row) => row.id}
              state={table.state}
              onStateChange={table.setState}
              total={runs.total}
              totalPages={runs.totalPages}
              searchPlaceholder="Buscar por recurso o usuario…"
              isFetching={runsQuery.isFetching}
            />
          )}
        </DataBoundary>
      </Card>

      {errorRun !== null && <SyncErrorPanel run={errorRun} onClose={() => setErrorRun(null)} />}

      <Modal
        open={runDialogOpen}
        title="Ejecutar sincronización"
        onClose={() => setRunDialogOpen(false)}
        footer={
          <>
            <Button onClick={() => setRunDialogOpen(false)}>Cancelar</Button>
            <Button variant="primary" onClick={() => setConfirmOpen(true)}>
              Continuar
            </Button>
          </>
        }
      >
        <div className="form-grid">
          <SelectField
            label="Fuente"
            value={source}
            onChange={(event) => setSource(event.target.value as SyncSource)}
          >
            {(Object.keys(SYNC_SOURCE_LABEL) as SyncSource[]).map((key) => (
              <option key={key} value={key}>
                {SYNC_SOURCE_LABEL[key]}
              </option>
            ))}
          </SelectField>
          <TextField
            label="Desde"
            type="date"
            value={from}
            hint="Vacío = período vigente."
            onChange={(event) => setFrom(event.target.value)}
          />
          <TextField
            label="Hasta"
            type="date"
            value={to}
            hint="Vacío = hasta hoy."
            onChange={(event) => setTo(event.target.value)}
          />
        </div>
        <Notice tone="info">
          La sincronización sólo lee de BUK. Ninguna operación escribe en el sistema de origen.
        </Notice>
      </Modal>

      <ConfirmDialog
        open={confirmOpen}
        title="Confirmar ejecución"
        confirmLabel="Ejecutar"
        loading={executeMutation.isPending}
        message={
          <>
            Se iniciará una sincronización de <strong>{SYNC_SOURCE_LABEL[source]}</strong>
            {from.length > 0 || to.length > 0
              ? ` para el rango ${from.length > 0 ? from : 'inicio'} – ${to.length > 0 ? to : 'hoy'}`
              : ' para el período vigente'}
            . El proceso puede tardar varios minutos y quedará registrado con su usuario.
          </>
        }
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => executeMutation.mutate()}
      />
    </>
  );
}

/** Detalle de errores de una ejecución. Se muestran íntegros, sin agrupar. */
function SyncErrorPanel({ run, onClose }: { run: SyncRun; onClose: () => void }): JSX.Element {
  const table = useTableState({ pageSize: 25 });

  const errorsQuery = useQuery({
    queryKey: queryKeys.syncErrors(run.id, table.state),
    queryFn: () => fetchSyncRunErrors(run.id, table.state),
  });

  const columns = useMemo<Array<Column<SyncRunError>>>(
    () => [
      {
        key: 'occurredAt',
        header: 'Momento',
        render: (row) => formatDateTime(row.occurredAt),
        plain: (row) => row.occurredAt,
      },
      {
        key: 'resource',
        header: 'Recurso',
        render: (row) => <span className="mono">{row.resource}</span>,
        plain: (row) => row.resource,
      },
      {
        key: 'page',
        header: 'Página',
        align: 'right',
        render: (row) => (row.page === null ? '—' : formatNumber(row.page)),
        plain: (row) => row.page,
      },
      {
        key: 'code',
        header: 'Código',
        render: (row) => <span className="mono">{row.code}</span>,
        plain: (row) => row.code,
      },
      {
        key: 'externalId',
        header: 'Id externo',
        render: (row) => (row.externalId === null ? '—' : <span className="mono">{row.externalId}</span>),
        plain: (row) => row.externalId,
      },
      {
        key: 'message',
        header: 'Mensaje',
        render: (row) => row.message,
        plain: (row) => row.message,
      },
    ],
    [],
  );

  const errors = errorsQuery.data;

  return (
    <Modal
      open
      size="wide"
      title={`Errores de la ejecución · ${run.sourceLabel} · ${run.resource}`}
      onClose={onClose}
      footer={<Button onClick={onClose}>Cerrar</Button>}
    >
      <Notice tone="error">
        <strong>{formatNumber(Math.max(run.errorCount, run.recordsFailed))} error(es).</strong> Los
        mensajes están sanitizados —sin secretos ni RUT— pero no resumidos: cada fallo aparece con su
        recurso y página para poder reproducirlo.
      </Notice>

      <DataBoundary
        isLoading={errorsQuery.isLoading}
        isError={errorsQuery.isError}
        error={errorsQuery.error}
        onRetry={() => void errorsQuery.refetch()}
        isEmpty={errors !== undefined && errors.total === 0}
        empty={{
          title: 'Sin detalle disponible',
          message:
            'La ejecución registró errores en su resumen, pero no hay registros de detalle asociados. Informe esta inconsistencia al equipo técnico.',
        }}
        skeleton={<TableSkeleton columns={6} rows={6} />}
      >
        {errors !== undefined && (
          <DataTable
            caption="Errores de la ejecución"
            columns={columns}
            rows={errors.data}
            getRowId={(row) => row.id}
            state={table.state}
            onStateChange={table.setState}
            total={errors.total}
            totalPages={errors.totalPages}
            searchable={false}
          />
        )}
      </DataBoundary>
    </Modal>
  );
}
